import { Paper, ScrollArea, Button, ThemeIcon, Center, Loader, useMantineTheme, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconBuildingStore, IconTrash } from "@tabler/icons-react";
import cx from "clsx";

import { ListVendorDto } from "../../models/vendors.models";
import { useFilteredVendors } from "../../functions/vendor-filter.hooks";
import { useSearch } from "../../functions/SearchBox.hook";
import { NoSearchResults } from "../Shared/NoSearchResults";
import classes from "./VendorList.module.css";

interface VendorListProps {
	vendors?: ListVendorDto[] | undefined;
	isLoading?: boolean;
	selectedVendor?: ListVendorDto | null;
	setSelectedVendor?: (vendor: ListVendorDto | null) => void;
}

export const VendorList = ({ vendors, selectedVendor, setSelectedVendor, isLoading }: VendorListProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const { query, setQuery } = useSearch();
	const { filteredVendors, showNoResults } = useFilteredVendors(vendors || []);

	if (isLoading) return <Paper withBorder p={0} radius="md" style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? "unset" : "calc(100vh - 160px)", minHeight: isMobile ? "calc(100vh - 150px)" : "calc(100vh - 160px)" }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>;

	const handleVendorSelect = (e: React.MouseEvent<HTMLAnchorElement>, vendor: ListVendorDto): void => {
		e.preventDefault();
		setSelectedVendor?.(vendor);
	};

	return <>
		<Paper withBorder p={0} radius="md" style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? "unset" : "calc(100vh - 160px)", minHeight: isMobile ? "calc(100vh - 150px)" : "calc(100vh - 160px)" }}>
				{filteredVendors?.map(vendor => (
					<Button key={vendor.VendorId}
						component="a"
						href={`/vendors/${vendor.VendorId}`}
						onClick={(e) => handleVendorSelect(e, vendor)}
						className={cx(classes["vendor-list-item"], { [classes["selected-item"]]: selectedVendor?.VendorId === vendor.VendorId })}
						leftSection={
							<ThemeIcon radius="xl"
								color={vendor.Deleted ? "red" : (vendor.Colour || "blue")}
								size="xl"
								style={{ border: "1px solid var(--mantine-color-default-border)" }}
								variant="light"
							>
								{!vendor.Deleted ? <IconBuildingStore size={24} /> : <IconTrash size={24} />}
							</ThemeIcon>
						}
					>
						<Text fw={500} c={vendor.Deleted ? "dimmed" : ""}>{vendor.Name}</Text>
					</Button>
				))}
				{showNoResults && <NoSearchResults searchType="vendors" query={query} setQuery={setQuery} />}
				{vendors?.length === 0 && <>
					<Stack align="center" justify="center" mt="xl" px="sm">
						<ThemeIcon size={100} radius={100} variant="light" color="gray">
							<IconBuildingStore size={60} />
						</ThemeIcon>
						<Text style={{ textAlign: "center" }} lineClamp={1} c="dimmed">No vendors have been created yet.</Text>
					</Stack>
				</>}
			</ScrollArea>
		</Paper>
	</>;
};
